<template>
  <div
    v-lazyload-image="{ disable: disableLazyLoad }"
    :style="{ paddingTop: containerPadding + '%' }"
    :class="[
      'jtn-responsive-image',
      disableLazyLoad ? 'loaded': ''
    ]"
    @click="$emit('img-click', $event)"
  >
    <picture>
      <source
        v-for="source in smallImageSources"
        :key="source.srcset"
        :srcset="source.srcset"
        :type="source.type"
      />
      <img
        :data-flickity-lazyload-src="smallImage"
        :data-src="smallImage"
        class="small-img"
        alt=""
      >
    </picture>

    <picture class="picture-wrap">
      <source
        v-for="source in sources"
        :key="source.srcset"
        :data-srcset="source.srcset"
        :srcset="disableLazyLoad ? source.srcset : ''"
        :type="source.type"
        :sizes="source.size"
        :media="source.media"
      >
      <img
        :data-flickity-lazyload-src="defaultImage"
        :data-flickity-lazyload-srcset="srcset"
        :data-src="defaultImage"
        :src="disableLazyLoad ? defaultImage : ''"
        :data-srcset="srcset"
        :srcset="disableLazyLoad ? srcset : ''"
        :alt="alt"
        :class="[
          defaultClass,
          imageClass
        ]"
        :sizes="sizes"
        class="main-image"
      >
    </picture>
  </div>
</template>

<script>
import { WEBP_POSTFIX, getImageSources } from '@jtnews/shared';

/**
 * @deprecated Используем новый компонент JtnUiImage - packages/jtn-ui/src/components/jtn-ui-image/jtn-ui-image.tsx
 */
export default {
  name: 'JtnResponsiveImage',
  props: {
    disableLazyLoad: {
      type: Boolean,
      default: false
    },
    // the base with which to generate image urls, with placeholders for width and height
    // %width% and %height%
    // example: http://via.placeholder.com/%width%x%height%
    imageUrl: {
      type: String,
      required: true
    },
    mode: {
      type: String,
      default: 'all',
      validator(value) {
        // The value must match one of these strings -  tablet means tablet portrait, tablet landscape uses desktop sizes
        return ['all', 'tablet', 'smartphone', 'mobile'].indexOf(value) !== -1;
      }
    },
    // percentage of screen occupied by image, in numbers, defaults to 100
    widthOnScreen: {
      type: Number,
      default: 100
    },
    // percentage of screen occupied by image on tablet portrait, in numbers, if not set it uses witdhOnScreen
    widthOnScreenTablet: {
      type: Number,
      default: undefined
    },
    // percentage of screen occupied by image on smartphone, in numbers, if not set it uses witdhOnScreen
    widthOnScreenSmartphone: {
      type: Number,
      default: undefined
    },
    // ratio of height respective to width, defaults to 16/9
    imageRatio: {
      type: Number,
      default: 16 / 9
    },
    alt: {
      type: String,
      default: ''
    },
    imageClass: {
      type: String,
      default: ''
    },
    // the maximum width to consider for the image, if it is not possibile to generate an image wider
    // than a specific width
    maxWidth: {
      type: Number,
      default: 1920
    }
  },
  data: () => ({
      defaultClass: 'vue-responsive-image',
      // width placeholder
      widthPlaceholder: '%width%',
      // height placeholder
      heightPlaceholder: '%height%',
      // the base sizes on which to calculate final image width
      baseSizes: {
        desktop: [1920, 1600, 1440, 1366, 1024, 768],
        tabletPortrait: [1600, 1024, 768],
        smartphone: [818, 768, 640, 425]
      },
      // the default width of the image, a reasonable size for old browsers, mostly desktop, who don't support srcset
      defaultWidth: 1600
    }),
  computed: {
    // the small image in the src attribut
    containerPadding() {
      return 100 / this.imageRatio;
    },
    smallImage() {
      let finalWidth = this.getWidthAdaptedToWidthOnScreen(
        this.defaultWidth,
        this.widthOnScreen
      );
      finalWidth = finalWidth > this.maxWidth ? this.maxWidth : finalWidth;
      finalWidth = Math.floor(finalWidth / 5);
      return this.getImageUrlWithWidthAndHeight(
        finalWidth,
        this.getHeightFromWidth(finalWidth)
      );
    },
    smallImageSources() {
      return getImageSources(this.smallImage);
    },
    // the default image in the src attribute, for backwards compatibility
    defaultImage() {
      let finalWidth = this.getWidthAdaptedToWidthOnScreen(
        this.defaultWidth,
        this.widthOnScreen
      );
      finalWidth = finalWidth > this.maxWidth ? this.maxWidth : finalWidth;
      return this.getImageUrlWithWidthAndHeight(
        finalWidth,
        this.getHeightFromWidth(finalWidth)
      );
    },
    // only if there's a specific tablet width which is different from the desktop one
    hasTabletSrcset() {
      return (
        typeof this.widthOnScreenTablet !== 'undefined'
        && this.widthOnScreenTablet !== this.widthOnScreen
      );
    },
    // only if there's a specific smartphone width which is different from the desktop one
    hasSmartphoneSrcset() {
      return (
        typeof this.widthOnScreenSmartphone !== 'undefined'
        && this.widthOnScreenSmartphone !== this.widthOnScreen
      );
    },
    sizes() {
      return `${this.widthOnScreen}vw`;
    },
    tabletSizes() {
      return `${this.widthOnScreenTablet}vw`;
    },
    smartphoneSizes() {
      return `${this.widthOnScreenSmartphone}vw`;
    },
    srcscetSizes() {
      // check mode
      return this.getSrcsetSizes(this.mode);
    },
    srcset() {
      return this.getSrcset();
    },
    sources() {
      const sources = [];

      if (this.hasSmartphoneSrcset) {
        const smartphoneSource = {
          srcset: this.getSmartphoneSrcset(),
          media: '(max-width: 425px)',
          size: this.smartphoneSizes
        };

        const smartphoneSources = [
          {
            ...smartphoneSource,
            srcset: this.getSmartphoneSrcset(WEBP_POSTFIX),
            type: 'image/webp'
          },
          {
            ...smartphoneSource
          }
        ]

        sources.push(...smartphoneSources);
      }

      if (this.hasTabletSrcset) {
        const tabletSource = {
          srcset: this.getTabletSrcset(),
          media: '(max-width: 767px)',
          size: this.tabletSizes
        };

        const tabletSources = [
          {
            ...tabletSource,
            srcset: this.getTabletSrcset(WEBP_POSTFIX),
            type: 'image/webp'
          },
          {
            ...tabletSource
          }
        ];

        sources.push(...tabletSources);
      }

      const defaultSource = {
        srcset: this.getSrcset(),
        size: this.sizes
      };

      const defaultSources = [
        {
          ...defaultSource,
          srcset: this.getSrcset(WEBP_POSTFIX),
          type: 'image/webp'
        },
        {
          ...defaultSource
        }
      ]

      sources.push(...defaultSources);

      return sources;
    }
  },
  methods: {
    getSrcsetSizes(mode) {
      let res;
      switch (mode) {
        case 'tablet': // portrait
          res = this.baseSizes.tabletPortrait;
          break;
        case 'smartphone':
          res = this.baseSizes.smartphone;
          break;
        case 'mobile':
          res = [
            ...new Set([
              ...this.baseSizes.tabletPortrait,
              ...this.baseSizes.smartphone
            ])
          ];
          break;
        case 'desktop':
          res = this.baseSizes.desktop;
          break;
        case 'all':
        default:
          res = [
            ...new Set([
              ...this.baseSizes.desktop,
              ...this.baseSizes.tabletPortrait,
              ...this.baseSizes.smartphone
            ])
          ];
      }

      return res;
    },
    getSmartphoneSrcset(postfix = '') {
      return this.getSrcsetSizes('smartphone')
        .map(width => {
          let finalWidth = this.getWidthAdaptedToWidthOnScreen(
            width,
            this.widthOnScreenSmartphone
          );
          finalWidth = finalWidth > this.maxWidth ? this.maxWidth : finalWidth;
          const result = `${this.getImageUrlWithWidthAndHeight(
              finalWidth,
              this.getHeightFromWidth(finalWidth)
            )}${postfix} ${finalWidth}w`;
          return result;
        })
        .join(', ');
    },
    getTabletSrcset(postfix = '') {
      return this.getSrcsetSizes('tablet')
        .map(width => {
          let finalWidth = this.getWidthAdaptedToWidthOnScreen(
            width,
            this.widthOnScreenTablet
          );
          finalWidth = finalWidth > this.maxWidth ? this.maxWidth : finalWidth;
          return (
            `${this.getImageUrlWithWidthAndHeight(
              finalWidth,
              this.getHeightFromWidth(finalWidth)
            )}${postfix} ${finalWidth}w`
          );
        })
        .join(', ');
    },
    getSrcset(postfix = '') {
      // if no different widths are defined for tablet and desktop, produce images for all versions requested in mode parameter
      if (
        typeof this.widthOnScreenTablet === 'undefined'
        && typeof this.widthOnScreenSmartphone === 'undefined'
      ) {
        return this.srcscetSizes
          .map(width => {
            let finalWidth = this.getWidthAdaptedToWidthOnScreen(
              width,
              this.widthOnScreen
            );
            finalWidth = finalWidth > this.maxWidth ? this.maxWidth : finalWidth;
            return (
              `${this.getImageUrlWithWidthAndHeight(
                finalWidth,
                this.getHeightFromWidth(finalWidth)
              )}${postfix} ${finalWidth}w`
            );
          })
          .join(', ');
      }
        // produce images only for desktop
        return this.getSrcsetSizes('desktop')
          .map(width => {
            let finalWidth = this.getWidthAdaptedToWidthOnScreen(
              width,
              this.widthOnScreenTablet
            );
            finalWidth = finalWidth > this.maxWidth ? this.maxWidth : finalWidth;
            return (
              `${this.getImageUrlWithWidthAndHeight(
                finalWidth,
                this.getHeightFromWidth(finalWidth)
              )}${postfix} ${finalWidth}w`
            );
          })
          .join(', ');
    },
    getHeightFromWidth(width) {
      return Math.round(width / this.imageRatio);
    },
    getWidthAdaptedToWidthOnScreen(width, widthOnScreen = 100) {
      if (widthOnScreen >= 100) return width;
      return Math.round((width / 100) * widthOnScreen);
    },
    getImageUrlWithWidthAndHeight(width, height) {
      return this.imageUrl
        .replace(this.widthPlaceholder, width)
        .replace(this.heightPlaceholder, height);
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'styles';

.jtn-responsive-image {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  width: 100%;

  background: $color-surface-200;

  &.loaded {
    .main-image {
      opacity: 1;
    }
  }

  & img {
    position: absolute;

    max-width: 100vw;
    max-height: 100vh;
    object-fit: contain;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);

    opacity: 0;

    transition: opacity 0.2s ease-in;

    &.loaded {
      opacity: 1;

      &.small-img {
        filter: blur(0.4rem);
        width: 99%;
        height: auto;
      }
    }
  }

  & .main-image {
    top: 50%;
    left: 50%;

    width: 100%;
    height: auto;

    filter: none;

    transform: translate(-50%, -50%);
  }

  & .zoomer-container {
    width: 100%;
    height: 100%;
  }
}
</style>
